const PrivacyPolicy = () => (
  <div id="privacy" className="scroll-mt-24">
    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2 pb-4 border-b-2 border-violet-500">
      Privacy Policy
    </h2>

    <p className="text-base text-gray-700 leading-relaxed mb-10">
      At <strong>AzzMatrix</strong> (&quot;AzzMatrix,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), accessible through <strong>azzmatrix.com</strong>, we value the privacy of our customers and website visitors. This Privacy Policy explains what customer information we collect, how we use it, how it is stored, who has access to it, and why we retain it. By using our website, you agree to the terms of this Privacy Policy.
    </p>

    <div className="space-y-10">

      {/* Section 1 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-database-2-line text-violet-600 mr-3 mt-1"></i>
          1. Information We Collect
        </h3>
        <div className="space-y-4 text-gray-700 leading-relaxed">
          <div>
            <p className="font-semibold text-slate-800 mb-2">a. Personal Information</p>
            <p className="mb-3">When a customer places an order, contacts us, or submits information through our website, we may collect:</p>
            <ul className="space-y-2 ml-6">
              {['Full name', 'Email address', 'Phone number', 'Billing address', 'Shipping address', 'Order details', 'Any information voluntarily submitted through contact forms or customer service inquiries'].map((item) => (
                <li key={item} className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800 mb-2">b. Payment Information</p>
            <p className="mb-3">We accept payments by credit card; however, <strong>AzzMatrix does not store, process, or retain full credit card information on its website or servers</strong>. All credit card payments are processed securely through our third-party payment gateway provider, <strong>Authorize.net</strong>. We only receive limited transaction-related information such as:</p>
            <ul className="space-y-2 ml-6">
              {['Payment status', 'Transaction ID or reference number', 'Limited billing information as needed for order verification and customer support'].map((item) => (
                <li key={item} className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800 mb-2">c. Technical and Website Usage Information</p>
            <p className="mb-3">We may automatically collect limited non-sensitive technical information such as:</p>
            <ul className="space-y-2 ml-6">
              {['IP address', 'Browser type', 'Device type', 'Pages visited', 'Date and time of access', 'Referring website or source'].map((item) => (
                <li key={item} className="flex items-start">
                  <i className="ri-arrow-right-s-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Section 2 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-focus-3-line text-violet-600 mr-3 mt-1"></i>
          2. Purpose of Collecting and Storing Customer Information
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3">We collect and store customer information only for legitimate business purposes, including:</p>
        <ul className="space-y-2 ml-6 text-gray-700">
          {[
            'Processing and fulfilling customer orders',
            'Delivering products or services',
            'Communicating with customers regarding purchases, inquiries, or support requests',
            'Sending order confirmations, invoices, and service updates',
            'Preventing fraudulent or unauthorized transactions',
            'Maintaining internal business records',
            'Improving website functionality, customer service, and user experience',
            'Complying with applicable legal, tax, accounting, and regulatory obligations',
          ].map((item) => (
            <li key={item} className="flex items-start">
              <i className="ri-checkbox-circle-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-700 leading-relaxed mt-4">We do <strong>not</strong> collect or store customer information for any unlawful or unrelated purpose.</p>
      </div>

      {/* Section 3 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-server-line text-violet-600 mr-3 mt-1"></i>
          3. How Customer Information Is Stored
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3">Customer information is stored using commercially reasonable administrative, technical, and physical safeguards. Information may be stored:</p>
        <ul className="space-y-2 ml-6 text-gray-700 mb-4">
          {[
            'On secure website hosting infrastructure',
            'In secure business email systems',
            'In internal business systems used for order processing, customer service, and recordkeeping',
            'Through trusted third-party service providers that support our website operations and payment processing',
          ].map((item) => (
            <li key={item} className="flex items-start">
              <i className="ri-arrow-right-s-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div className="bg-violet-50 border-l-4 border-violet-500 p-4 rounded-lg">
          <p className="text-sm text-gray-700 leading-relaxed">
            <strong className="text-violet-900">Note:</strong> Credit card information is not stored on the AzzMatrix website or its servers. Payment card transactions are handled by paypal or <strong>Authorize.net</strong>, a third-party payment processor. We retain customer information only for as long as reasonably necessary to fulfill the purposes described in this policy or as required by law.
          </p>
        </div>
      </div>

      {/* Section 4 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-user-settings-line text-violet-600 mr-3 mt-1"></i>
          4. Who Has Access to Customer Information
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3">Access to customer information is restricted to authorized persons and service providers who need the information for legitimate business purposes. This may include:</p>
        <ul className="space-y-2 ml-6 text-gray-700 mb-4">
          {[
            'Authorized AzzMatrix management or staff',
            'Website administrators or technical support personnel',
            'Payment processor(s), including paypal, Authorize.net',
            'Service providers involved in website hosting, order fulfillment, communications, or business operations',
            'Legal, accounting, or regulatory authorities where disclosure is required by law',
          ].map((item) => (
            <li key={item} className="flex items-start">
              <i className="ri-arrow-right-s-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-700 leading-relaxed">We do <strong>not</strong> sell customer personal information to third parties.</p>
      </div>

      {/* Section 5 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-share-line text-violet-600 mr-3 mt-1"></i>
          5. Sharing of Information
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3">We may share customer information only when necessary to operate our business, including:</p>
        <ul className="space-y-2 ml-6 text-gray-700">
          {[
            'To process payments through Authorize.net or paypal',
            'To complete order fulfillment and delivery',
            'To provide customer support',
            'To protect our business, customers, or website from fraud or misuse',
            'To comply with legal obligations, court orders, subpoenas, or lawful government requests',
            'In connection with a business transfer, merger, sale, or acquisition, where permitted by law',
          ].map((item) => (
            <li key={item} className="flex items-start">
              <i className="ri-arrow-right-s-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-700 leading-relaxed mt-4">We do <strong>not</strong> share customer information with unrelated third parties for their independent marketing purposes.</p>
      </div>

      {/* Section 6 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-bank-card-line text-violet-600 mr-3 mt-1"></i>
          6. Payment Processing Disclosure
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3">For all card transactions made on <strong>azzmatrix.com</strong>:</p>
        <ul className="space-y-2 ml-6 text-gray-700">
          {[
            'Credit card payments are processed through Paypal or Authorize.net',
            'AzzMatrix does not store customers\' full credit card numbers, CVV codes, or complete cardholder payment data on its website',
            'Payment data is handled by our payment gateway provider in accordance with its own security and privacy practices',
          ].map((item) => (
            <li key={item} className="flex items-start">
              <i className="ri-checkbox-circle-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-700 leading-relaxed mt-4">Customers may review Paypal&apos;s or&nbsp; Authorize.net&apos;s privacy practices directly through their official website.</p>
      </div>

      {/* Section 7 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-settings-3-line text-violet-600 mr-3 mt-1"></i>
          7. Cookies and Tracking Technologies
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3">Our website may use cookies or similar technologies to:</p>
        <ul className="space-y-2 ml-6 text-gray-700 mb-4">
          {[
            'Support website functionality',
            'Remember user preferences',
            'Improve site performance',
            'Analyze website traffic and visitor behavior',
          ].map((item) => (
            <li key={item} className="flex items-start">
              <i className="ri-arrow-right-s-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-700 leading-relaxed">Cookies do not generally collect sensitive financial information. Visitors may adjust browser settings to refuse certain cookies, though some site features may not function properly.</p>
      </div>

      {/* Section 8 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-shield-keyhole-line text-violet-600 mr-3 mt-1"></i>
          8. Data Security
        </h3>
        <p className="text-gray-700 leading-relaxed">
          We take reasonable steps to protect customer information. While no system can guarantee absolute security, we implement safeguards intended to reduce the risk of unauthorized access, disclosure, or misuse. Customers are encouraged to use secure devices and internet connections when submitting information online.
        </p>
      </div>

      {/* Section 9 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-user-heart-line text-violet-600 mr-3 mt-1"></i>
          9. Customer Choices and Rights
        </h3>
        <p className="text-gray-700 leading-relaxed mb-3">Customers may contact us to:</p>
        <ul className="space-y-2 ml-6 text-gray-700 mb-4">
          {[
            'Request access to their personal information',
            'Correct inaccurate information',
            'Request deletion of information where appropriate and legally permitted',
            'Ask questions about how their information is used',
          ].map((item) => (
            <li key={item} className="flex items-start">
              <i className="ri-checkbox-circle-fill text-violet-600 mr-2 mt-1 flex-shrink-0"></i>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-700 leading-relaxed">We will respond to such requests in accordance with applicable law and our business obligations.</p>
      </div>

      {/* Section 10 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-parent-line text-violet-600 mr-3 mt-1"></i>
          10. Children&apos;s Privacy
        </h3>
        <p className="text-gray-700 leading-relaxed">
          Our website is not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13. If we become aware that such information has been collected, we will take reasonable steps to delete it.
        </p>
      </div>

      {/* Section 11 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-external-link-line text-violet-600 mr-3 mt-1"></i>
          11. Third-Party Services and Links
        </h3>
        <p className="text-gray-700 leading-relaxed">
          Our website may contain links to third-party websites or use third-party services. We are not responsible for the privacy, security, or content practices of third-party websites or providers. Customers should review the privacy policies of those third parties separately.
        </p>
      </div>

      {/* Section 12 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-refresh-line text-violet-600 mr-3 mt-1"></i>
          12. Changes to This Privacy Policy
        </h3>
        <p className="text-gray-700 leading-relaxed">
          We may update this Privacy Policy from time to time to reflect changes in our operations, legal requirements, or website functionality. Any updates will be posted on this page with a revised effective date.
        </p>
      </div>

      {/* Section 13 */}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 flex items-start">
          <i className="ri-contacts-line text-violet-600 mr-3 mt-1"></i>
          13. Contact Information
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          If you have any questions about this Privacy Policy or our handling of customer information, please contact us:
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <p className="text-gray-700 mb-2"><strong className="text-slate-900">AzzMatrix</strong></p>
          <p className="text-gray-700 mb-2">
            <i className="ri-global-line text-violet-600 mr-2"></i>
            Website: <a href="https://azzmatrix.com" rel="nofollow" className="text-violet-600 hover:text-violet-700 font-semibold">azzmatrix.com</a>
          </p>
          <p className="text-gray-700 mb-2">
            <i className="ri-mail-line text-violet-600 mr-2"></i>
            Email: <a href="mailto:support@azzmatrix.com" className="text-violet-600 hover:text-violet-700 font-semibold">support@azzmatrix.com</a>
          </p>
          <p className="text-gray-700 mb-2">
            <i className="ri-map-pin-line text-violet-600 mr-2"></i>
            Address: 1178 Colfax Ave, Des Plaines, IL
          </p>
          <p className="text-gray-700">
            <i className="ri-phone-line text-violet-600 mr-2"></i>
            Phone: <a href="tel:+18729998632" className="text-violet-600 hover:text-violet-700 font-semibold">+1 (872) 999-8632</a>
          </p>
        </div>
      </div>

    </div>
  </div>
);

export default PrivacyPolicy;
